// Copyright (c) 2026 CTO's Club LLC. All rights reserved.

import { z } from 'zod';

declare const UserEmailIdentityBrand: unique symbol;

export type UserEmailIdentity = string & {
    readonly [UserEmailIdentityBrand]: true;
};

export function normalizeUserEmailInputForSubmission(emailAddress: string): string {
    const trimmedEmailAddress: string = emailAddress.trim();
    const firstDomainSeparatorIndex: number = trimmedEmailAddress.indexOf('@');
    const lastDomainSeparatorIndex: number = trimmedEmailAddress.lastIndexOf('@');
    const hasIdentifiableDomain: boolean =
        firstDomainSeparatorIndex > 0 &&
        firstDomainSeparatorIndex === lastDomainSeparatorIndex &&
        firstDomainSeparatorIndex < trimmedEmailAddress.length - 1;

    if (!hasIdentifiableDomain) {
        return trimmedEmailAddress;
    }

    const localPart: string = trimmedEmailAddress.slice(0, firstDomainSeparatorIndex);
    const domain: string = trimmedEmailAddress.slice(firstDomainSeparatorIndex + 1);
    return `${localPart}@${domain.toLowerCase()}`;
}

export const UserEmailIdentitySchema: z.ZodType<UserEmailIdentity> = z
    .string()
    .email()
    .refine(
        (emailAddress: string): emailAddress is UserEmailIdentity => {
            const domainSeparatorIndex: number = emailAddress.lastIndexOf('@');
            const domain: string = emailAddress.slice(domainSeparatorIndex + 1);
            return domain === domain.toLowerCase();
        },
        {
            message: 'Email domain must use lowercase letters',
        }
    );
